import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type FiltersParamType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'enum';

export type FiltersDefaultValueMap = {
  string: string;
  number: number;
  boolean: boolean;
  date: Date | null;
  enum: string | number | null;
};

export type FiltersParamConfig<
  T extends FiltersParamType,
  E extends string | number = never
> = {
  pageKey: string;
  paramKey: string;
  type: T;
  defaultValue?: T extends 'enum' ? E | null : FiltersDefaultValueMap[T];
  enumValues?: T extends 'enum' ? E[] : never;
};

export type FiltersValueFromType<
  T extends FiltersParamType,
  E extends string | number = never
> = T extends 'enum' ? E | null : FiltersDefaultValueMap[T];

const FILTERS_DEFAULT_VALUES: FiltersDefaultValueMap = {
  string: '',
  number: 0,
  boolean: false,
  date: null,
  enum: null,
};

function parseFiltersValue(
  raw: string | null,
  config: FiltersParamConfig<any, any>
) {
  const { type, enumValues } = config;
  const defaultValue =
    config.defaultValue ??
    FILTERS_DEFAULT_VALUES[type as keyof FiltersDefaultValueMap];

  if (raw === null) return defaultValue;

  switch (type) {
    case 'number':
      return Number(raw) || defaultValue;
    case 'boolean':
      return raw === 'true';
    case 'date':
      return raw ? new Date(raw) : defaultValue;
    case 'enum':
      if (enumValues) {
        const numeric = Number(raw);
        const valid = enumValues.includes(raw) || enumValues.includes(numeric);
        return valid ? raw : defaultValue;
      }
      return defaultValue;
    default:
      return raw;
  }
}

function serializeFiltersValue(type: FiltersParamType, value: any) {
  if (type === 'date') return value ? new Date(value).toISOString() : '';
  return String(value);
}

export type FiltersStoreState = {
  pageParams: Record<string, Record<string, any>>; // prefix -> key -> value
  pageConfigs: Record<string, Record<string, FiltersParamConfig<any, any>>>;

  registerParam: <
    T extends FiltersParamType,
    E extends string | number = never
  >(
    config: FiltersParamConfig<T, E>
  ) => void;

  setParam: (prefix: string, key: string, value: any) => void;
  resetParam: (prefix: string, key: string) => void;
  resetAll: () => void;
};

export const useFiltersStore = create<FiltersStoreState>()(
  persist(
    (set, get) => ({
      pageParams: {},
      pageConfigs: {},

      registerParam: <
        T extends FiltersParamType,
        E extends string | number = never
      >(
        config: FiltersParamConfig<T, E>
      ) => {
        const { pageKey, paramKey } = config;

        const params = new URLSearchParams(window.location.search);
        const queryValue = params.get(paramKey);

        const existingPageParams = get().pageParams[pageKey] || {};
        const storedValue = existingPageParams[paramKey];

        const parsedValue = parseFiltersValue(
          queryValue ??
            (storedValue !== undefined ? String(storedValue) : null),
          config
        );

        set((state) => ({
          pageConfigs: {
            ...state.pageConfigs,
            [pageKey]: {
              ...(state.pageConfigs[pageKey] || {}),
              [paramKey]: config,
            },
          },
          pageParams: {
            ...state.pageParams,
            [pageKey]: {
              ...(state.pageParams[pageKey] || {}),
              [paramKey]: parsedValue,
            },
          },
        }));
      },

      setParam: (prefix, key, value) => {
        const config = get().pageConfigs[prefix]?.[key];
        if (!config) return;

        const serializedValue = serializeFiltersValue(config.type, value);

        // Update URL
        const params = new URLSearchParams(window.location.search);
        if (!serializedValue) params.delete(key);
        else params.set(key, serializedValue);

        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?${params}`
        );

        // Update store
        set((state) => ({
          pageParams: {
            ...state.pageParams,
            [prefix]: { ...(state.pageParams[prefix] || {}), [key]: value },
          },
        }));
      },

      resetParam: (prefix, key) => {
        const config = get().pageConfigs[prefix]?.[key];
        if (!config) return;

        const defaultValue =
          config.defaultValue ??
          FILTERS_DEFAULT_VALUES[config.type as keyof FiltersDefaultValueMap];

        get().setParam(prefix, key, defaultValue);
      },
      resetAll: () => {
        useFiltersStore.persist.clearStorage();
        set({
          pageParams: {},
          pageConfigs: {},
        });
      },
    }),
    {
      name: 'filters-store',
      partialize: (state) => ({ pageParams: state.pageParams }),
    }
  )
);
